import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Sun, CheckCircle2 } from "lucide-react";
import api from "../../lib/api";

function genSessionId(scope) {
  // Reuse same session per browser tab so multi-turn context survives a refresh.
  // Different scope (e.g. tracker token) gets its own session.
  try {
    const k = `blg_chat_sid:${scope || "default"}`;
    let v = sessionStorage.getItem(k);
    if (!v) {
      v = `web_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem(k, v);
    }
    return v;
  } catch {
    return `web_${Date.now()}`;
  }
}

const GENERIC_GREETING = "Hi! I'm Surya, the AI assistant at Purvodaya Solar ☀. Curious about rooftop solar or the PM Surya Ghar subsidy? Ask me anything!";
const ASSISTANT_GREETING = "Hi! I'm Surya, your Purvodaya Solar digital assistant ☀. Ask me about your enquiry stage, upcoming appointments, your assigned consultant, or request a callback.";

export default function ChatWidget({ token, customerName }) {
  const assistantMode = !!token;
  const greeting = assistantMode
    ? `Hi${customerName ? ` ${customerName}` : ""}! I'm Surya, your Purvodaya Solar digital assistant ☀. Ask me about your enquiry stage, upcoming appointments, your assigned consultant, or request a callback.`
    : GENERIC_GREETING;

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: greeting }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [captured, setCaptured] = useState(false);
  const [callbackOk, setCallbackOk] = useState(false);
  const sidRef = useRef(genSessionId(token || "anon"));
  const scrollRef = useRef(null);

  // Suggested prompts in assistant mode
  const suggested = assistantMode
    ? ["What stage is my enquiry in?", "When is my site visit?", "Who is my consultant?", "Request a callback"]
    : [];

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, open]);

  async function sendText(text) {
    const t = (text || "").trim();
    if (!t || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: t }]);
    setSending(true);
    try {
      const body = { session_id: sidRef.current, message: t };
      if (token) body.token = token;
      const { data } = await api.post("/public/chat", body);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      if (data.lead_captured) setCaptured(true);
      if (data.callback_requested) setCallbackOk(true);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Hmm, I'm having a glitch. Please try again in a moment." },
      ]);
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-open-btn"
          className="fixed bottom-5 right-5 z-50 bg-[#1F4E2C] hover:bg-[#0E2818] text-white rounded-full shadow-2xl p-4 transition-all hover:scale-110 group"
        >
          <MessageCircle className="w-6 h-6" strokeWidth={2} />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-[#D4FF7A] rounded-full animate-pulse"></span>
          <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-[#0E2818] text-white text-[10px] font-mono uppercase tracking-widest px-2 py-1 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
            {assistantMode ? "Ask Surya ☀" : "Chat with Surya ☀"}
          </span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed inset-x-0 bottom-0 sm:inset-x-auto sm:bottom-5 sm:right-5 z-50 sm:w-[380px] sm:rounded-md overflow-hidden shadow-2xl flex flex-col bg-white border border-[#1F4E2C]/20"
          style={{ maxHeight: "min(640px, 90vh)" }}
          data-testid="chat-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-[#1F4E2C] to-[#0E2818] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 bg-[#D4FF7A] rounded-full flex items-center justify-center">
                <Sun className="w-5 h-5 text-[#0E2818]" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-display font-bold text-sm leading-tight">Surya</div>
                <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#7DD181]">
                  {assistantMode ? "Your Digital Assistant" : "Purvodaya Solar"}
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="chat-close-btn"
              className="text-white/70 hover:text-white p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F4F6F1]"
            data-testid="chat-messages"
          >
            {messages.map((m, i) => (
              <div key={i} className={"flex " + (m.role === "user" ? "justify-end" : "justify-start")}>
                <div
                  className={
                    "max-w-[85%] px-3 py-2 text-sm leading-relaxed " +
                    (m.role === "user"
                      ? "bg-[#1F4E2C] text-white rounded-2xl rounded-br-sm"
                      : "bg-white text-[#0F1A12] border border-[#DDE5D8] rounded-2xl rounded-bl-sm")
                  }
                  data-testid={`chat-msg-${m.role}-${i}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#DDE5D8] rounded-2xl rounded-bl-sm px-3 py-2 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-[#1F4E2C]" />
                  <span className="text-xs font-mono text-[#475569]">Surya is typing…</span>
                </div>
              </div>
            )}
            {captured && (
              <div
                className="bg-emerald-50 border border-emerald-200 px-3 py-2 rounded text-xs flex items-center gap-2"
                data-testid="chat-lead-captured"
              >
                <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                <span className="text-emerald-900">Got it! Our solar expert will reach out soon. ☀</span>
              </div>
            )}
            {callbackOk && (
              <div
                className="bg-emerald-50 border border-emerald-200 px-3 py-2 rounded text-xs flex items-center gap-2"
                data-testid="chat-callback-requested"
              >
                <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                <span className="text-emerald-900">Callback noted — your consultant will get back to you. ☀</span>
              </div>
            )}

            {/* Suggested prompts (assistant mode, only when chat is just the greeting) */}
            {assistantMode && messages.length === 1 && (
              <div className="flex flex-wrap gap-1.5 pt-1" data-testid="chat-suggestions">
                {suggested.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => sendText(s)}
                    className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-[#1F4E2C]/40 text-[#1F4E2C] hover:bg-[#1F4E2C] hover:text-white transition-colors"
                    data-testid={`chat-suggest-${s.replace(/\W+/g, "-").toLowerCase()}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendText(input);
            }}
            className="border-t border-[#DDE5D8] bg-white px-3 py-3 flex items-center gap-2"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={assistantMode ? "Ask about your enquiry…" : "Ask about solar, subsidy, savings…"}
              disabled={sending}
              data-testid="chat-input"
              className="flex-1 px-3 py-2 text-sm font-mono border border-[#DDE5D8] focus:border-[#1F4E2C] outline-none rounded"
              maxLength={2000}
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              data-testid="chat-send-btn"
              className="bg-[#1F4E2C] hover:bg-[#0E2818] text-white p-2 disabled:opacity-40 rounded transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
